import { resumeAgentHeartbeat } from "@/api/agents";
import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { getDurableConversation } from "@/api/conversations";
import { controlTaskWorker } from "@/api/task-workers";
import { moveWorkToast } from "@/lib/work-toast-item";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { releaseCursorControlForExecution } from "@/desktop/use-cursor-control";
import { restoreAndFocusCurrentWindow } from "@/desktop/use-global-hotkey";
import { workToastExecutionId } from "@/lib/computer-toast-key";
import { decideApproval, type ApprovalDecision } from "@/lib/decide-approval";
import { translate } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { isTauri } from "@/lib/is-tauri";
import { openDurableConversationFrame } from "@/lib/open-durable-conversation-frame";
import { endToolApproval } from "@/lib/work-toast-tool-approvals";
import { useAgentStore } from "@/stores/agent-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useNotificationStore } from "@/stores/notification-store";
import {
  useWorkToastStore,
  type WorkToastItem,
  type WorkToastOpenTarget,
} from "@/stores/work-toast-store";

/** The server's own error text when it sent one, else the thrown message. */
function errorDescription(error: unknown): string {
  const detail = (error as { data?: { error?: unknown } } | null)?.data?.error;
  const message = (error as { message?: unknown } | null)?.message;
  if (typeof detail === "string") return detail;
  if (typeof message === "string") return message;
  return String(error);
}

function reportFailure(id: string, title: string, error: unknown): void {
  const description = errorDescription(error);
  useNotificationStore
    .getState()
    .addSystemToast({ id, level: "error", title, description });
}

function focusDesktopWindow(): void {
  if (isTauri()) restoreAndFocusCurrentWindow();
}

/** Server replies that mean there is nothing left to stop (stale run, no
 *  sub-agent, or a runtime the backend can no longer reach). The last
 *  pattern is the backend's `NATIVE_RUNTIME_OFFLINE_MARKER` — every
 *  `native_runtime_unavailable_reason` string carries it verbatim. */
const NOTHING_TO_STOP = [
  /no sub-agent/i,
  /run changed/i,
  /historical/i,
  /sub-agent is offline/i,
];

/** Settle a working pill the server has nothing for — same end state as a
 *  successful stop, so ghosts don't hang with a Stop that can't land. */
function settleGhostToast(key: string): void {
  const store = useWorkToastStore.getState();
  const current = store.items[key];
  if (current && current.state === "working") {
    moveWorkToast(
      { workToastStore: useWorkToastStore },
      current,
      "stopped",
      Date.now(),
    );
  }
}

function explainStopNotSent(key: string, description: string): void {
  useNotificationStore.getState().addSystemToast({
    id: `work-toast-stop-${key}`,
    title: translate(toastsMessages, "stopFailedTitle"),
    description,
    level: "warning",
  });
}

/** Stop is first-degree and stays in Denker (spec §6.4). */
export async function stopWorkToast(item: WorkToastItem): Promise<void> {
  const executionId = workToastExecutionId(item);
  // The run's desktop control is halted on this machine first, so it stops
  // even when the stop itself cannot be sent.
  await releaseCursorControlForExecution(executionId);
  if (item.source === "native_worker") {
    if (!item.projectId || !item.taskId) {
      explainStopNotSent(item.key, translate(toastsMessages, "stopLostTask"));
      return;
    }
    const { projectId, taskId } = item;
    try {
      await controlTaskWorker(projectId, taskId, "pause", executionId);
    } catch (error) {
      // controlTaskWorker's apiRequest call isn't silent, so the global
      // error middleware already reported this; don't double-toast it.
      // But when the server says there is nothing to stop, also settle the
      // pill — otherwise a stale card hangs with a dead Stop button.
      const reason = errorDescription(error);
      if (NOTHING_TO_STOP.some((pattern) => pattern.test(reason))) {
        settleGhostToast(item.key);
      }
    }
    return;
  }
  // The socket drops `execution.stop` silently while it is not open, and a
  // mirror window always holds a ws shim, so the connection state decides.
  const ws = getCurrentDenkerWs();
  if (!ws || useCompanionStore.getState().wsConnectionState !== "connected") {
    explainStopNotSent(item.key, translate(toastsMessages, "stopNotConnected"));
    return;
  }
  ws.stopExecution(executionId);
}

export async function openConversationChat(
  conversationId: string,
  errorKey: string,
  shouldContinue: () => boolean = () => true,
): Promise<boolean> {
  const isCurrentScope = captureDesktopChatAuthScopeGuard();
  const stillCurrent = () => isCurrentScope() && shouldContinue();
  if (!stillCurrent()) return false;
  focusDesktopWindow();
  try {
    const conversation = await getDurableConversation(conversationId);
    if (!stillCurrent()) return false;
    await openDurableConversationFrame({
      conversation,
      addToast: useNotificationStore.getState().addSystemToast,
      closePopover: () => {},
      selectAgent: useAgentStore.getState().selectAgent,
      shouldContinue: stillCurrent,
    });
    return stillCurrent();
  } catch (error) {
    if (!stillCurrent()) return false;
    const toastId = `work-toast-open-chat-${errorKey}`;
    reportFailure(toastId, translate(toastsMessages, "openChatFailed"), error);
    return false;
  }
}
export async function openWorkToastChat(item: WorkToastItem): Promise<void> {
  if (!item.conversationId) return;
  await openConversationChat(item.conversationId, item.key);
}

/** Verify-in-app targets: https pages plus app protocol links (e.g. spotify:). Anything else is ignored. */
const OPEN_TARGET_SCHEMES =
  /^(https:|spotify:|notion:|slack:|linear:|figma:|msteams:|ms-outlook:|mailto:)/i;
export function openWorkToastTarget(url: string): void {
  const trimmed = url.trim();
  if (!OPEN_TARGET_SCHEMES.test(trimmed)) return;
  window.open(trimmed, "_blank", "noopener,noreferrer");
}

/** Explicit destination links open independently of the conversation frame. */
export function openWorkToastAppTarget(target: WorkToastOpenTarget): void {
  const uri = target.uri ?? target.url;
  if (uri) openWorkToastTarget(uri);
}

/** The stack cannot use `useLayout()`, so master-layout consumes this request.
 *  Work pills and task-sourced system toasts both open their task here. */
export function requestWorkToastTask(
  item: Pick<WorkToastItem, "projectId" | "taskId">,
): void {
  if (!item.projectId || !item.taskId) return;
  if (isTauri() && useDesktopMode.getState().mode !== "denk") {
    useDesktopMode.getState().openWorkspace();
    focusDesktopWindow();
  }
  useWorkToastStore
    .getState()
    .requestOpenTask({ projectId: item.projectId, taskId: item.taskId });
}

export async function decideWorkToastApproval(
  item: WorkToastItem,
  decision: ApprovalDecision,
): Promise<void> {
  const { approval, projectId, taskId } = item;
  if (!approval || !projectId || !taskId) return;
  const { approvalId } = approval;
  const context = { workToastStore: useWorkToastStore };
  try {
    await decideApproval({ projectId, taskId, approvalId }, decision);
  } catch (error) {
    // The apiRequest error middleware surfaces the failure; keep the pill so
    // the user can retry, unless the server says the tool approval is over.
    const status = (error as { status?: unknown } | null)?.status;
    if (status === 400 || status === 404) endToolApproval(context, approvalId);
    return;
  }
  endToolApproval(context, approvalId);
}

/** Resume a heartbeat the rolling-24h cap suspended, from the toast that
 *  announced it. Rejects on failure so the pill stays up for a retry — the
 *  apiRequest error middleware has already reported the reason. The durable
 *  path is agent settings, which reads the same suspension off the runtime. */
export async function resumeHeartbeatToast(item: {
  toastId: string;
  projectId: string;
  projectAgentId: string;
}): Promise<void> {
  await resumeAgentHeartbeat(item.projectId, item.projectAgentId);
  useNotificationStore.getState().removeSystemToast(item.toastId);
}

export {
  answerWorkToastQuestion,
  sendWorkToastFollowUp,
} from "./work-toast-reply-actions";
